import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { leadsApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus,
  HiOutlineSearch,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineEye,
  HiOutlinePencil,
  HiOutlineDocumentText,
  HiOutlineTrash,
  HiX
} from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import LeadForm from '../../components/Forms/LeadForm';
import TruncatedName from '../../components/TruncatedName';
import { usePermissions } from '../../hooks/usePermissions';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { getCached } from '../../services/lookupCache';
import PermissionWrapper from '../../components/PermissionWrapper';
import {
  formatDateTimeShortIST,
  calculateDetailedAgeIST,
  todayYmdIST,
} from '../../utils/timezoneDisplay';
import { INLINE_FILTER_BAR, INLINE_FILTER_FIELD } from '../../utils/inlineListFilters';

const Leads = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  // Only show a blocking spinner for the very first load; subsequent refetches
  // keep showing the previous list so the UI doesn't feel like it's "hanging".
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: searchParams.get('status') || '',
    source: '',
    inquiry_type: '',
    follow_up_pending: false,
    date_from: '',
    date_to: '',
    page: 1
  });
  const [pagination, setPagination] = useState({});
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [followUpData, setFollowUpData] = useState({
    date: '',
    time: '09:00'
  });
  const [deletingLeadId, setDeletingLeadId] = useState(null);

  const debouncedSearch = useDebouncedValue(filters.search, 300);

  useEffect(() => {
    fetchFiltersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters.status, filters.source, filters.inquiry_type, filters.follow_up_pending, filters.date_from, filters.date_to, filters.page]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = {
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.source ? { source: filters.source } : {}),
        ...(filters.inquiry_type ? { inquiry_type: filters.inquiry_type } : {}),
        ...(filters.follow_up_pending ? { follow_up_pending: 'true' } : {}),
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        page: filters.page,
        limit: 20
      };
      const response = await leadsApi.getAll(params);
      setLeads(response.data.data.leads);
      setPagination(response.data.data.pagination);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  };

  const fetchFiltersData = async () => {
    try {
      const [sourcesRes, statusesRes] = await Promise.all([
        getCached('leads:sources', () => leadsApi.getSources()),
        getCached('leads:statuses', () => leadsApi.getStatuses()),
      ]);
      setSources(sourcesRes.data.data);
      setStatuses(statusesRes.data.data);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };


  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value, page: 1 });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: searchParams.get('status') || '',
      source: '',
      inquiry_type: '',
      follow_up_pending: false,
      date_from: '',
      date_to: '',
      page: 1,
    });
  };

  const hasActiveFilters =
    filters.search ||
    filters.status ||
    filters.source ||
    filters.inquiry_type ||
    filters.follow_up_pending ||
    filters.date_from ||
    filters.date_to;

  const handleStatusChange = async (leadId, newStatusId) => {
    // Check if the new status requires follow-up date
    const newStatus = statuses.find(s => s.id === newStatusId);
    const requiresFollowUp = newStatus && ['Follow-up', 'DNP'].includes(newStatus.name);

    if (requiresFollowUp) {
      // Show modal to get follow-up date
      setPendingStatusChange({ leadId, statusId: newStatusId });
      setShowFollowUpModal(true);
    } else {
      // Update status directly without follow-up
      try {
        await leadsApi.updateStatus(leadId, { status_id: newStatusId });
        toast.success('Status updated');
        fetchLeads();
      } catch (error) {
        toast.error('Failed to update status');
      }
    }
  };

  const handleFollowUpSubmit = async () => {
    if (!followUpData.date) {
      toast.error('Please select a follow-up date');
      return;
    }

    if (!pendingStatusChange) return;

    try {
      const { leadId, statusId } = pendingStatusChange;
      const updateData = {
        status_id: statusId,
        next_follow_up: followUpData.date,
        ...(followUpData.time ? { next_follow_up_time: `${followUpData.time}:00` } : {})
      };

      await leadsApi.updateStatus(leadId, updateData);
      toast.success('Status updated and follow-up appointment created');
      setShowFollowUpModal(false);
      setPendingStatusChange(null);
      setFollowUpData({ date: '', time: '09:00' });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleCreateAssessment = (lead) => {
    // Navigate to assessments page with lead data to pre-fill assessment form
    navigate('/assessments', { 
      state: { 
        fromLead: true, 
        leadData: {
          lead_id: lead.id, // Store lead_id to link assessment to lead
          full_name: lead.full_name,
          dob: lead.dob,
          gender: lead.gender,
          address: lead.address,
          contact_primary: lead.contact_primary,
          contact_whatsapp: lead.contact_whatsapp,
          email: lead.email
        }
      } 
    });
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedLead(null);
    fetchLeads();
  };

  const handleDeleteLead = async (lead) => {
    if (!canDelete('leads')) {
      toast.error('You do not have permission to delete leads');
      return;
    }
    const label = lead.full_name ? ` "${lead.full_name}"` : '';
    if (!window.confirm(`Delete lead${label}? This cannot be undone.`)) {
      return;
    }
    try {
      setDeletingLeadId(lead.id);
      await leadsApi.delete(lead.id);
      toast.success('Lead deleted');
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete lead');
    } finally {
      setDeletingLeadId(null);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-500 mt-1">Manage and track your leads</p>
        </div>
        <PermissionWrapper module="leads" action="create">
            <button
              onClick={() => setShowForm(true)}
              className="btn btn-primary"
            >
              <HiOutlinePlus className="w-5 h-5 mr-2" />
              New Lead
            </button>
        </PermissionWrapper>
      </div>

      <div className={INLINE_FILTER_BAR}>
        <div className="relative min-w-[9rem] flex-1 max-w-[14rem]">
          <HiOutlineSearch className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            placeholder="Search…"
            className={`${INLINE_FILTER_FIELD} w-full pl-8`}
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-auto min-w-[6.5rem] max-w-[8.5rem]`}
          aria-label="Status"
        >
          <option value="">All status</option>
          {statuses.map((status) => (
            <option key={status.id} value={status.name}>
              {status.name}
            </option>
          ))}
        </select>
        <select
          value={filters.source}
          onChange={(e) => handleFilterChange('source', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-auto min-w-[6.5rem] max-w-[8.5rem]`}
          aria-label="Source"
        >
          <option value="">All sources</option>
          {sources.map((source) => (
            <option key={source.id} value={source.name}>
              {source.name}
            </option>
          ))}
        </select>
        <select
          value={filters.inquiry_type}
          onChange={(e) => handleFilterChange('inquiry_type', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-auto min-w-[6.5rem] max-w-[9rem]`}
          aria-label="Inquiry type"
        >
          <option value="">All types</option>
          <option value="therapy">Therapy</option>
          <option value="assessment">Assessment</option>
          <option value="consultation">Consultation</option>
          <option value="special_case">Special Case</option>
        </select>
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => handleFilterChange('date_from', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
          aria-label="From date"
        />
        <span className="text-xs text-gray-400">–</span>
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => handleFilterChange('date_to', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
          min={filters.date_from || undefined}
          aria-label="To date"
        />
        <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap px-0.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filters.follow_up_pending}
            onChange={(e) => handleFilterChange('follow_up_pending', e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Follow-up
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="whitespace-nowrap px-1 text-xs font-medium text-gray-500 hover:text-gray-800"
          >
            Clear
          </button>
        ) : null}
      </div>

      {/* Leads table */}
      <div className="card overflow-hidden p-0">
        {loading && !initialLoaded ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <HiOutlineDocumentText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>No leads found</p>
            <PermissionWrapper module="leads" action="create">
              <button onClick={() => setShowForm(true)} className="btn btn-primary mt-4">
                Add your first lead
              </button>
            </PermissionWrapper>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">Name</th>
                  <th className="table-header">Contact</th>
                  <th className="table-header">Inquiry</th>
                  <th className="table-header">Source</th>
                  <th className="table-header">Doctor Referral</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Created</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <TruncatedName name={lead.full_name} maxLength={20}>
                        <Link 
                          to={`/leads/${lead.id}`}
                          className="font-medium text-gray-900 hover:text-primary-600"
                        />
                      </TruncatedName>
                      {lead.dob && (
                        <p className="text-sm text-gray-500">
                          {calculateDetailedAgeIST(lead.dob)}
                        </p>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        <a href={`tel:${lead.contact_primary}`} className="p-1 hover:bg-gray-100 rounded">
                          <HiOutlinePhone className="w-4 h-4 text-gray-500" />
                        </a>
                        {lead.email && (
                          <a href={`mailto:${lead.email}`} className="p-1 hover:bg-gray-100 rounded">
                            <HiOutlineMail className="w-4 h-4 text-gray-500" />
                          </a>
                        )}
                        <span className="text-sm text-gray-600">{lead.contact_primary}</span>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className="capitalize">{lead.inquiry_type}</span>
                    </td>
                    <td className="table-cell text-gray-500">
                      {lead.source_name || '-'}
                    </td>
                    <td className="table-cell text-gray-500">
                      <TruncatedName name={lead.reference_details || '-'} maxLength={24} />
                    </td>
                    <td className="table-cell">
                      <select
                        value={lead.status_id}
                        onChange={(e) => handleStatusChange(lead.id, parseInt(e.target.value))}
                        className="text-sm border-0 bg-transparent focus:ring-0 cursor-pointer"
                        style={{ color: lead.status_color }}
                      >
                        {statuses.map(status => (
                          <option key={status.id} value={status.id}>{status.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="table-cell text-gray-500 text-sm">
                      {formatDateTimeShortIST(lead.created_at)}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/leads/${lead.id}`}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                          title="View"
                        >
                          <HiOutlineEye className="w-4 h-4 text-gray-500" />
                        </Link>
                        <PermissionWrapper module="leads" action="update">
                          <button
                            onClick={() => {
                              setSelectedLead(lead);
                              setShowForm(true);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            title="Edit"
                          >
                            <HiOutlinePencil className="w-4 h-4 text-gray-500" />
                          </button>
                        </PermissionWrapper>
                        {lead.status_name !== 'Enrolled' && (
                          <button
                            onClick={() => handleCreateAssessment(lead)}
                            className="p-2 hover:bg-blue-100 rounded-lg"
                            title="Create Assessment"
                          >
                            <HiOutlineDocumentText className="w-4 h-4 text-blue-600" />
                          </button>
                        )}
                        <PermissionWrapper module="leads" action="delete">
                          <button
                            type="button"
                            onClick={() => handleDeleteLead(lead)}
                            disabled={deletingLeadId === lead.id}
                            className="p-2 hover:bg-red-50 rounded-lg disabled:opacity-50"
                            title="Delete"
                          >
                            {deletingLeadId === lead.id ? (
                              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                            ) : (
                              <HiOutlineTrash className="w-4 h-4 text-red-600" />
                            )}
                          </button>
                        </PermissionWrapper>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={pagination.page === 1}
                className="btn btn-secondary text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={pagination.page >= pagination.totalPages}
                className="btn btn-secondary text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Lead Form Modal */}
      {showForm && (
        <LeadForm
          lead={selectedLead}
          sources={sources}
          statuses={statuses}
          onClose={() => {
            setShowForm(false);
            setSelectedLead(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Follow-up Date Modal */}
      {showFollowUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Set Follow-up Date</h3>
              <button
                onClick={() => {
                  setShowFollowUpModal(false);
                  setPendingStatusChange(null);
                  setFollowUpData({ date: '', time: '09:00' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                This status requires a follow-up date. Please select when you'd like to follow up with this lead.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Follow-up Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={followUpData.date}
                  onChange={(e) => setFollowUpData({ ...followUpData, date: e.target.value })}
                  min={todayYmdIST()}
                  className="input w-full"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Cannot select past dates</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Follow-up Time
                </label>
                <input
                  type="time"
                  value={followUpData.time}
                  onChange={(e) => setFollowUpData({ ...followUpData, time: e.target.value })}
                  className="input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Default: 09:00</p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowFollowUpModal(false);
                  setPendingStatusChange(null);
                  setFollowUpData({ date: '', time: '09:00' });
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFollowUpSubmit}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Save & Update Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;

